import React, { useState, useEffect } from "react";
import { useAuth, API, formatApiErrorDetail } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Settings as SettingsIcon, Plus, Edit2, Trash2, Loader2, Users, Shield, FileText } from "lucide-react";

const Settings = () => {
  const { user, getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState("positions");
  const [positions, setPositions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Position dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [newPositionName, setNewPositionName] = useState("");
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [editName, setEditName] = useState("");
  
  // Role change dialog
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [newRole, setNewRole] = useState("");
  
  // Template dialogs
  const [showDeleteTemplateDialog, setShowDeleteTemplateDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPositions();
    fetchEmployees();
    fetchTemplates();
  }, []);

  const fetchPositions = async () => {
    try {
      const response = await axios.get(`${API}/positions`, {
        headers: getAuthHeaders(),
        
      });
      setPositions(response.data);
    } catch (error) {
      console.error("Error fetching positions:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`, {
        headers: getAuthHeaders(),
        
      });
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/shift-templates`, {
        headers: getAuthHeaders(),
        
      });
      setTemplates(response.data);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  // Position handlers
  const handleAddPosition = async () => {
    if (!newPositionName.trim()) {
      toast.error("Please enter a position name");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/positions`,
        { name: newPositionName.trim() },
        { headers: getAuthHeaders() }
      );
      toast.success("Position added successfully!");
      setShowAddDialog(false);
      setNewPositionName("");
      fetchPositions();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to add position");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditPosition = async () => {
    if (!editName.trim()) {
      toast.error("Please enter a position name");
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(
        `${API}/positions/${selectedPosition.id}`,
        { name: editName.trim() },
        { headers: getAuthHeaders() }
      );
      toast.success("Position updated successfully!");
      setShowEditDialog(false);
      setSelectedPosition(null);
      setEditName("");
      fetchPositions();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to update position");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePosition = async () => {
    setSubmitting(true);
    try {
      await axios.delete(`${API}/positions/${selectedPosition.id}`, {
        headers: getAuthHeaders(),
        
      });
      toast.success("Position deleted successfully!");
      setShowDeleteDialog(false);
      setSelectedPosition(null);
      fetchPositions();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to delete position");
    } finally {
      setSubmitting(false);
    }
  };

  // Role handlers
  const handleRoleChange = async () => {
    if (!newRole) {
      toast.error("Please select a role");
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(
        `${API}/employees/${selectedEmployee.id}/role`,
        { user_id: selectedEmployee.id, new_role: newRole },
        { headers: getAuthHeaders() }
      );
      toast.success(`Role updated to ${newRole}!`);
      setShowRoleDialog(false);
      setSelectedEmployee(null);
      setNewRole("");
      fetchEmployees();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to update role");
    } finally {
      setSubmitting(false);
    }
  };

  // Template handlers
  const handleDeleteTemplate = async () => {
    setSubmitting(true);
    try {
      await axios.delete(`${API}/shift-templates/${selectedTemplate.id}`, {
        headers: getAuthHeaders(),
        
      });
      toast.success("Template deleted successfully!");
      setShowDeleteTemplateDialog(false);
      setSelectedTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to delete template");
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (position) => {
    setSelectedPosition(position);
    setEditName(position.name);
    setShowEditDialog(true);
  };

  const openDeleteDialog = (position) => {
    setSelectedPosition(position);
    setShowDeleteDialog(true);
  };

  const openRoleDialog = (employee) => {
    setSelectedEmployee(employee);
    setNewRole(employee.role);
    setShowRoleDialog(true);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin":
        return "bg-[#6A1E2E] text-white";
      case "supervisor":
        return "bg-[#E55934] text-white";
      default:
        return "bg-[#F0EAEB] text-[#6A1E2E]";
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1A1A1A]">Settings</h1>
          <p className="text-[#666666]">Manage system configuration</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="border-b border-[#E5E5E5] bg-transparent w-full justify-start rounded-none p-0">
          <TabsTrigger
            value="positions"
            className="data-[state=active]:border-b-2 data-[state=active]:border-[#6A1E2E] data-[state=active]:text-[#6A1E2E] rounded-none"
            data-testid="tab-positions"
          >
            <SettingsIcon className="w-4 h-4 mr-2" />
            Positions
          </TabsTrigger>
          <TabsTrigger
            value="roles"
            className="data-[state=active]:border-b-2 data-[state=active]:border-[#6A1E2E] data-[state=active]:text-[#6A1E2E] rounded-none"
            data-testid="tab-roles"
          >
            <Shield className="w-4 h-4 mr-2" />
            User Roles
          </TabsTrigger>
          <TabsTrigger
            value="templates"
            className="data-[state=active]:border-b-2 data-[state=active]:border-[#6A1E2E] data-[state=active]:text-[#6A1E2E] rounded-none"
            data-testid="tab-templates"
          >
            <FileText className="w-4 h-4 mr-2" />
            Shift Templates
          </TabsTrigger>
        </TabsList>

        {/* Positions Tab */}
        <TabsContent value="positions" className="mt-6">
          <Card className="border border-[#E5E5E5] shadow-none">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="flex items-center gap-2 text-[#1A1A1A]">
                <SettingsIcon className="w-5 h-5 text-[#6A1E2E]" />
                Positions
              </CardTitle>
              <Button
                onClick={() => setShowAddDialog(true)}
                className="bg-[#6A1E2E] hover:bg-[#521422] text-white"
                data-testid="add-position-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Position
              </Button>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#666666] mb-4">
                Manage employee positions that can be assigned when adding employees or shifts.
              </p>
              {loading ? (
                <div className="text-center py-8 text-[#666666]">Loading positions...</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="table-header-uppercase">Position Name</TableHead>
                      <TableHead className="table-header-uppercase text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {positions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} className="text-center text-[#999999]">
                          No positions defined. Add one to get started.
                        </TableCell>
                      </TableRow>
                    ) : (
                      positions.map((pos) => (
                        <TableRow key={pos.id} data-testid={`position-row-${pos.id}`}>
                          <TableCell className="font-medium text-[#1A1A1A]">{pos.name}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(pos)}
                                className="text-[#666666] hover:text-[#6A1E2E] hover:bg-[#F0EAEB]"
                                data-testid={`edit-position-${pos.id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDeleteDialog(pos)}
                                className="text-[#666666] hover:text-red-600 hover:bg-red-50"
                                data-testid={`delete-position-${pos.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* User Roles Tab */}
        <TabsContent value="roles" className="mt-6">
          <Card className="border border-[#E5E5E5] shadow-none">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-[#1A1A1A]">
                <Shield className="w-5 h-5 text-[#6A1E2E]" />
                User Roles Management
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#666666] mb-4">
                Change user roles between Employee, Supervisor, and Administrator.
              </p>
              <div className="flex gap-4 mb-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 rounded-sm bg-[#6A1E2E] text-white text-xs">Admin</span>
                  <span className="text-[#666666]">Full system access</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 rounded-sm bg-[#E55934] text-white text-xs">Supervisor</span>
                  <span className="text-[#666666]">Can manage shifts & approve requests</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 rounded-sm bg-[#F0EAEB] text-[#6A1E2E] text-xs">Employee</span>
                  <span className="text-[#666666]">Basic access</span>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="table-header-uppercase">Name</TableHead>
                    <TableHead className="table-header-uppercase">Email</TableHead>
                    <TableHead className="table-header-uppercase">Current Role</TableHead>
                    <TableHead className="table-header-uppercase text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.map((emp) => (
                    <TableRow key={emp.id} data-testid={`role-row-${emp.id}`}>
                      <TableCell className="font-medium text-[#1A1A1A]">
                        {emp.first_name} {emp.last_name}
                      </TableCell>
                      <TableCell className="text-[#666666]">{emp.email}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 text-xs font-medium rounded-sm ${getRoleBadgeColor(emp.role)}`}>
                          {emp.role?.charAt(0).toUpperCase() + emp.role?.slice(1) || "Employee"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {emp.id !== user?.id && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openRoleDialog(emp)}
                            className="border-[#E5E5E5]"
                            data-testid={`change-role-${emp.id}`}
                          >
                            Change Role
                          </Button>
                        )}
                        {emp.id === user?.id && (
                          <span className="text-xs text-[#999999]">Current user</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="mt-6">
          <Card className="border border-[#E5E5E5] shadow-none">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-[#1A1A1A]">
                <FileText className="w-5 h-5 text-[#6A1E2E]" />
                Shift Templates
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#666666] mb-4">
                Manage saved shift templates. Templates can be created from the Add Shift page.
              </p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="table-header-uppercase">Template Name</TableHead>
                    <TableHead className="table-header-uppercase">Event</TableHead>
                    <TableHead className="table-header-uppercase">Time</TableHead>
                    <TableHead className="table-header-uppercase">Positions</TableHead>
                    <TableHead className="table-header-uppercase text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-[#999999]">
                        No templates saved. Create one from the Add Shift page.
                      </TableCell>
                    </TableRow>
                  ) : (
                    templates.map((template) => (
                      <TableRow key={template.id} data-testid={`template-row-${template.id}`}>
                        <TableCell className="font-medium text-[#1A1A1A]">{template.name}</TableCell>
                        <TableCell>{template.event_name}</TableCell>
                        <TableCell className="font-mono text-sm">
                          {template.start_time} - {template.end_time}
                        </TableCell>
                        <TableCell>
                          {template.positions_needed?.join(", ") || "Any"}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedTemplate(template);
                              setShowDeleteTemplateDialog(true);
                            }}
                            className="text-[#666666] hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-template-${template.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Position Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Add New Position</DialogTitle>
            <DialogDescription>Enter a name for the new position.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input
              placeholder="Position name"
              value={newPositionName}
              onChange={(e) => setNewPositionName(e.target.value)}
              className="border-[#E5E5E5] focus:border-[#6A1E2E]"
              data-testid="new-position-input"
            />
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowAddDialog(false)} className="border-[#E5E5E5]">
              Cancel
            </Button>
            <Button
              onClick={handleAddPosition}
              disabled={submitting}
              className="bg-[#6A1E2E] hover:bg-[#521422] text-white"
              data-testid="submit-new-position"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Position"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Position Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Position</DialogTitle>
            <DialogDescription>Update the position name.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input
              placeholder="Position name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="border-[#E5E5E5] focus:border-[#6A1E2E]"
              data-testid="edit-position-input"
            />
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="border-[#E5E5E5]">
              Cancel
            </Button>
            <Button
              onClick={handleEditPosition}
              disabled={submitting}
              className="bg-[#6A1E2E] hover:bg-[#521422] text-white"
              data-testid="submit-edit-position"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Position Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Delete Position</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedPosition?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="border-[#E5E5E5]">
              Cancel
            </Button>
            <Button
              onClick={handleDeletePosition}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-position"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Change User Role</DialogTitle>
            <DialogDescription>
              Change the role for {selectedEmployee?.first_name} {selectedEmployee?.last_name}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger className="border-[#E5E5E5]" data-testid="role-select">
                <SelectValue placeholder="Select new role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="employee">Employee</SelectItem>
                <SelectItem value="supervisor">Supervisor</SelectItem>
                <SelectItem value="admin">Administrator</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowRoleDialog(false)} className="border-[#E5E5E5]">
              Cancel
            </Button>
            <Button
              onClick={handleRoleChange}
              disabled={submitting}
              className="bg-[#6A1E2E] hover:bg-[#521422] text-white"
              data-testid="confirm-role-change"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update Role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Template Dialog */}
      <Dialog open={showDeleteTemplateDialog} onOpenChange={setShowDeleteTemplateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Delete Template</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedTemplate?.name}"?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDeleteTemplateDialog(false)} className="border-[#E5E5E5]">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteTemplate}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-template"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
